import type { LineBox, POS, TextLine } from "@/models/Viewer";

export interface TextSelection {
  /** A box for each text line that has selected characters, in page order. */
  lines: LineBox[];
  /** Index of the first selected character in the text of the page. */
  startIndex: number;
  /** The number of selected characters: the length of `selectedText`. */
  textLength: number;
  /** The text of the page from `startIndex`, `textLength` characters long. */
  selectedText: string;
}

/** A point of a drag, as the text line it is on and how far along that line: `Infinity` is the whole line. */
interface Position {
  line: number;
  x: number;
}

/** The characters of a text line that a selection covers: their rectangles, `first` to `last`, and their extent. */
interface Covered {
  first: number;
  last: number;
  left: number;
  right: number;
}

// A point this close to the edge of a box is on it: scaling the box and the point leaves floating point noise.
const EDGE_TOLERANCE = 1e-6;

// Lines whose tops, or bottoms, are less than this apart are on the same row when looking for the line next to a gap.
const SAME_ROW = 1;

export const emptySelection = (): TextSelection => ({ lines: [], startIndex: 0, textLength: 0, selectedText: "" });

function scaleLines(textLines: TextLine[], annotationScale: number): LineBox[] {
  return textLines.map(({ x, y, width, height }) => ({
    x: x / annotationScale,
    y: y / annotationScale,
    width: width / annotationScale,
    height: height / annotationScale,
  }));
}

/** How far `x` is from the box of `line`: 0 inside it. */
function distanceX(line: LineBox, x: number) {
  return Math.max(line.x - x, x - (line.x + line.width), 0);
}

/** Lines are beside each other when they are not over each other and are on the same rows (at most half a line apart). */
function areBeside(a: LineBox, b: LineBox) {
  const overlapX = a.x < b.x + b.width && b.x < a.x + a.width;
  const apart = Math.max(a.y, b.y) - Math.min(a.y + a.height, b.y + b.height);
  return !overlapX && apart <= Math.min(a.height, b.height) / 2;
}

/**
 * The line whose vertical extent contains `pos`, or -1, leaving out the lines marked in `excluded`. When several do
 * (rows whose boxes overlap, separate lines on one row) the one that also contains its x wins, and of those the one
 * whose vertical centre is nearest. When none contains its x the one nearest horizontally wins, so a point beyond the
 * end of a row is at the end of its nearest line, and then the one whose vertical centre is nearest. The first in page
 * order breaks any remaining tie.
 */
function lineAt(lines: LineBox[], excluded: boolean[] | undefined, { x, y }: POS) {
  let best = -1;
  let bestOutside = 0;
  let bestPrimary = 0;
  let bestSecondary = 0;
  for (let index = 0; index < lines.length; index++) {
    const line = lines[index];
    if (excluded?.[index] === true) continue;
    if (y < line.y - EDGE_TOLERANCE || y > line.y + line.height + EDGE_TOLERANCE) continue;

    // A line that contains x ranks by how near it is vertically, then horizontally (0); any other by the reverse
    const across = distanceX(line, x);
    const vertical = Math.abs(y - (line.y + line.height / 2));
    const outside = across > EDGE_TOLERANCE ? 1 : 0;
    const primary = outside === 1 ? across : vertical;
    const secondary = outside === 1 ? vertical : across;

    const nearer =
      best < 0 ||
      outside < bestOutside ||
      (outside === bestOutside && (primary < bestPrimary || (primary === bestPrimary && secondary < bestSecondary)));
    if (!nearer) continue;

    best = index;
    bestOutside = outside;
    bestPrimary = primary;
    bestSecondary = secondary;
  }
  return best;
}

/** How far the box of `line` is from `y`: below `y` when `below`, else above it. 0 or less when it is not that way. */
function distanceBeyond(line: LineBox, y: number, below: boolean) {
  return below ? line.y - y : y - (line.y + line.height);
}

/**
 * The line just below (or above) a point that is in a gap between lines, or -1 when there is none, leaving out the
 * lines marked in `excluded`. Lines on the same row tie, and then the one nearest horizontally, then the first in page
 * order, wins.
 */
function lineNextTo(lines: LineBox[], excluded: boolean[], { x, y }: POS, below: boolean) {
  let nearest = Infinity;
  for (let index = 0; index < lines.length; index++) {
    if (excluded[index]) continue;

    const distance = distanceBeyond(lines[index], y, below);
    if (distance > 0 && distance < nearest) nearest = distance;
  }

  let best = -1;
  let bestAcross = 0;
  for (let index = 0; index < lines.length; index++) {
    if (excluded[index]) continue;

    const line = lines[index];
    const distance = distanceBeyond(line, y, below);
    if (!(distance > 0 && distance - nearest < SAME_ROW)) continue;

    const across = distanceX(line, x);
    if (best < 0 || across < bestAcross) {
      best = index;
      bestAcross = across;
    }
  }
  return best;
}

/**
 * Where `pos` is in the text. A point on a line keeps its x. A point between the lines of its column, or beyond the end
 * of a line, is on the row of that line when it is in its height, and in a gap otherwise: a start in a gap (or above all
 * the text) takes the line below it, still at its x, and an end takes the whole line above it. A start below all the
 * text and an end above all of it are nowhere. When the lines of another column sit next to the column the point is in,
 * as they do when two columns are half a row apart, they are not where the point is: the point is in the gap of its own
 * column, not on a row of the other.
 */
function positionOf(lines: LineBox[], pos: POS, role: "start" | "end"): Position | null {
  const on = lineAt(lines, undefined, pos);
  if (on >= 0 && distanceX(lines[on], pos.x) <= EDGE_TOLERANCE) return { line: on, x: pos.x };

  const column = lines.filter((line) => distanceX(line, pos.x) <= EDGE_TOLERANCE);
  const excluded = lines.map((line) => column.some((other) => areBeside(other, line)));
  const row = lineAt(lines, excluded, pos);
  if (row >= 0) return { line: row, x: pos.x };

  const next = lineNextTo(lines, excluded, pos, role === "start");
  if (next < 0) return null;
  return { line: next, x: role === "start" ? pos.x : Infinity };
}

/**
 * The characters of `line` from the first whose centre is at or right of `from` to the last whose centre is left of
 * `to`, which are in the same coordinates as the text lines divided by `annotationScale`. Only a rectangle with text
 * counts as a character: not a line end, and not a tick box.
 */
function coveredBy(line: TextLine, annotationScale: number, from: number, to: number): Covered | null {
  const { rectangles, characters } = line;
  let first = -1;
  let last = -1;
  let left = Infinity;
  let right = -Infinity;
  for (let index = 0; index < rectangles.length; index++) {
    const rectangle = rectangles[index];
    const x = rectangle[0];
    const width = rectangle[2];
    if (width === 0 || characters[index] === "") continue;

    const centre = (x + width / 2) / annotationScale;
    if (first < 0 && centre < from) continue;
    if (centre >= to) break;

    if (first < 0) first = index;
    last = index;
    left = Math.min(left, x, x + width);
    right = Math.max(right, x, x + width);
  }
  return first < 0 ? null : { first, last, left, right };
}

/** The number of characters of the text of `line` before its rectangle `index`. */
function charactersBefore(line: TextLine, index: number) {
  let count = 0;
  for (let rectangle = 0; rectangle < index; rectangle++) count += line.characters[rectangle].length;
  return count;
}

/**
 * The text selected from `startPos` to `endPos`, which are in the same coordinates as the text lines divided by
 * `annotationScale`. `startPos` must be the upper point (and the left one on the same height); use `getDragSelection`
 * when the direction of the drag is not known.
 *
 * Each point is a position in the text (`positionOf`), found from where the lines are on the page, never from their
 * order in the text. The selection is every line in page order from the earlier position to the later one. A point on
 * a line, or a start in a gap, has an x: it trims the first line from there, the last line up to there, and a single
 * line at both, the left one starting it. An end in a gap has none: its line is whole wherever it is in page order,
 * selected to its end when it is the last line and from its start when it is the first. A line the selection reaches
 * that has no character covered is left out. A drag that ends above the line it starts on, because both points are in
 * the same gap, selects nothing.
 *
 * A character is selected when its centre is at or right of the start and left of the end, so a character the selection
 * covers by less than half is not, and the boxes are the extent of the selected characters.
 */
export function getTextSelection(
  textLines: TextLine[],
  annotationScale: number,
  startPos: POS,
  endPos: POS,
): TextSelection {
  const lines = scaleLines(textLines, annotationScale);
  const start = positionOf(lines, startPos, "start");
  const end = positionOf(lines, endPos, "end");
  if (start == null || end == null) return emptySelection();

  // The line below a gap and the line above it: the drag stayed within the gap
  const endLine = lines[end.line];
  if (endLine.y + endLine.height < lines[start.line].y) return emptySelection();

  const [first, last] =
    start.line < end.line || (start.line === end.line && start.x <= end.x) ? [start, end] : [end, start];

  const boxes: LineBox[] = [];
  let firstCovered = -1;
  let firstRectangle = -1;
  let lastCovered = -1;
  let lastRectangle = -1;
  for (let index = first.line; index <= last.line; index++) {
    // An end in a gap has no x to trim at: its line is whole, from its start when it is the first line in page order
    const from = index === first.line && first.x !== Infinity ? first.x : -Infinity;
    const to = index === last.line ? last.x : Infinity;
    const covered = coveredBy(textLines[index], annotationScale, from, to);
    if (covered == null) continue;

    if (firstCovered < 0) {
      firstCovered = index;
      firstRectangle = covered.first;
    }
    lastCovered = index;
    lastRectangle = covered.last;
    boxes.push({
      x: covered.left / annotationScale,
      y: lines[index].y,
      width: (covered.right - covered.left) / annotationScale,
      height: lines[index].height,
    });
  }
  if (firstCovered < 0) return emptySelection();

  const firstLine = textLines[firstCovered];
  const lastLine = textLines[lastCovered];
  const startIndex = firstLine.startIndex + charactersBefore(firstLine, firstRectangle);
  const endIndex =
    lastLine.startIndex + charactersBefore(lastLine, lastRectangle) + lastLine.characters[lastRectangle].length;

  // The lines tile the text of the page, so the lines in between are whole in it
  const selectedText =
    firstLine === lastLine
      ? firstLine.text.slice(startIndex - firstLine.startIndex, endIndex - firstLine.startIndex)
      : [
          firstLine.text.slice(startIndex - firstLine.startIndex),
          ...textLines.slice(firstCovered + 1, lastCovered).map(({ text }) => text),
          lastLine.text.slice(0, endIndex - lastLine.startIndex),
        ].join("");

  return { lines: boxes, startIndex, textLength: endIndex - startIndex, selectedText };
}

/**
 * The text selected by dragging between `pivot` (where the drag started) and `pointer` (where it is now), in either
 * direction: the upper of the two points starts the selection, and on one height the left one.
 */
export function getDragSelection(textLines: TextLine[], annotationScale: number, pivot: POS, pointer: POS) {
  const pointerFirst = pointer.y < pivot.y || (pointer.y === pivot.y && pointer.x < pivot.x);
  return pointerFirst
    ? getTextSelection(textLines, annotationScale, pointer, pivot)
    : getTextSelection(textLines, annotationScale, pivot, pointer);
}
